// src/layouts/MyUserMenu.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  UserMenu, 
  UserMenuProps, 
  useTranslate, 
  useGetIdentity,
  useLogout
} from 'react-admin';
import { 
  Box,
  Typography,
  Button,
  Avatar,
  Divider,
  Chip,
  Paper,
  Stack
} from '@mui/material';
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  AccessTime as TimeIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { decodeJWT } from '../utils/auth';
import ChangePasswordDialog from '../components/dialogs/ChangePasswordDialog';

interface MyUserMenuProps extends Omit<UserMenuProps, 'children'> {
  // Props adicionales si necesitas
}

export const MyUserMenu: React.FC<MyUserMenuProps> = (props) => {
  const translate = useTranslate();
  const { data: identity, isLoading } = useGetIdentity();
  const logout = useLogout();
  
  // Estado para el diálogo de cambio de contraseña
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  
  // Estado para el tiempo restante del token
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [tokenExpiryTime, setTokenExpiryTime] = useState<number | null>(null);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  // Función para formatear el tiempo restante
  const formatTimeRemaining = useCallback((seconds: number): string => {
    if (seconds <= 0) return translate('auth.session_expired', 'Sesión expirada');
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }, [translate]);

  // Función para calcular el tiempo restante del token
  const calculateTimeRemaining = useCallback(() => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setTimeRemaining('');
      return;
    }
    
    try {
      const decodedToken = decodeJWT(token);
      
      if (!decodedToken || !decodedToken.exp) {
        setTimeRemaining('');
        return;
      }
      
      const currentTime = Math.floor(Date.now() / 1000);
      const timeLeft = decodedToken.exp - currentTime;
      
      // Si el token ya expiró
      if (timeLeft <= 0) {
        setTimeRemaining(translate('auth.session_expired', 'Sesión expirada'));
        setIsExpiringSoon(true);
        return;
      }
      
      // Si está próximo a expirar (menos de 5 minutos)
      if (timeLeft <= 300) {
        setIsExpiringSoon(true);
      } else {
        setIsExpiringSoon(false);
      }
      
      setTimeRemaining(formatTimeRemaining(timeLeft));
      setTokenExpiryTime(decodedToken.exp);
      
    } catch (error) {
      console.error('Error decoding token:', error);
      setTimeRemaining('');
    }
  }, [formatTimeRemaining, translate]);

  // Effect para actualizar el tiempo restante cada segundo
  useEffect(() => {
    // Cálculo inicial
    calculateTimeRemaining();
    
    // Actualizar cada segundo
    const interval = setInterval(calculateTimeRemaining, 1000);
    
    return () => clearInterval(interval);
  }, [calculateTimeRemaining]);

  // Función para manejar el click en "Change Password"
  const handleChangePasswordClick = () => {
    // Cerrar el menú
    const menuButton = document.querySelector('[aria-label="Profile"]');
    if (menuButton instanceof HTMLElement) {
      menuButton.click();
    }
    
    // Abrir el diálogo de cambio de contraseña
    setPasswordDialogOpen(true);
  };

  // Función para manejar el logout
  const handleLogout = () => {
    logout();
  };

  // Determinar el nombre del rol para mostrar
  const getRoleName = (role: string) => {
    const roleNames: Record<string, string> = {
      'SuperAdmin': 'Super Administrator',
      'GroupAdmin': 'Group Administrator',
      'HotelAdmin': 'Hotel Administrator',
      'Receptionist': 'Receptionist',
      'User': 'User'
    };
    return roleNames[role] || role;
  };

  if (isLoading) {
    return <UserMenu {...props} />;
  }

  const userName = identity?.name || identity?.username || 'User';
  const userRole = identity?.role || 'User';
  const userEmail = identity?.email || '';
  // Convertir identity.id a string si es necesario
  const userId = identity?.id ? String(identity.id) : undefined;

  return (
    <UserMenu {...props}>
      <Paper 
        sx={{ 
          p: 2, 
          width: 280,
          borderRadius: 2,
        }}
        elevation={0}
      >
        <Stack spacing={2}>
          {/* Header con Avatar y Info de Usuario */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 1 }}>
            <Avatar 
              sx={{ 
                width: 64, 
                height: 64, 
                bgcolor: 'grey.300',
                mb: 2
              }}
            >
              <PersonIcon sx={{ fontSize: 32, color: 'grey.600' }} />
            </Avatar>
            
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {getRoleName(userRole)}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {userName}
            </Typography>
            
            {/* Chip de tiempo restante */}
            {timeRemaining && (
              <Chip
                icon={<TimeIcon />}
                label={timeRemaining}
                size="small"
                color={isExpiringSoon ? "warning" : "success"}
                sx={{ 
                  fontWeight: 500,
                  '& .MuiChip-icon': {
                    fontSize: 16
                  }
                }}
              />
            )}
          </Box>

          <Divider />

          {/* Botones de acción */}
          <Stack spacing={1}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={handleChangePasswordClick}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: 'text.primary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {translate('user.change_password', 'Change Password')}
            </Button>
            
            <Button
              fullWidth
              variant="contained"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                backgroundColor: '#00697A',
                '&:hover': {
                  backgroundColor: '#004d5c'
                }
              }}
            >
              {translate('ra.auth.logout', 'Logout')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      
      {/* Diálogo de cambio de contraseña */}
      {passwordDialogOpen && (
        <ChangePasswordDialog
          open={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          userId={userId}
          userName={userName}
          isCurrentUser={true}
        />
      )}
    </UserMenu>
  );
};

export default MyUserMenu;